import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  MapTrifold, Warning, Coins, Target, TrendUp, ArrowLeft, ArrowRight, X, Check, Users,
} from "@phosphor-icons/react";
import api, { apiErr, HERO_MAP } from "../api";
import GoldCounter from "../components/GoldCounter";
import GuidanceBanner from "../components/GuidanceBanner";

const DIFF_COLOR = {
  Medium: "text-ff-brass border-ff-brass/40",
  "Medium-Hard": "text-amber-500 border-amber-500/40",
  Hard: "text-ff-danger border-ff-danger/50",
};

export default function PlayFlow() {
  const nav = useNavigate();
  const [step, setStep] = useState("map");
  const [expeditions, setExpeditions] = useState([]);
  const [crew, setCrew] = useState([]);
  const [balance, setBalance] = useState(0);
  const [selectedExp, setSelectedExp] = useState(null);
  const [selectedCrew, setSelectedCrew] = useState([]);
  const [estimate, setEstimate] = useState(null);
  const [stake, setStake] = useState(0);
  const [idemKey, setIdemKey] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/expeditions"), api.get("/crew"), api.get("/game/dashboard")])
      .then(([e, c, d]) => {
        setExpeditions(e.data);
        setCrew(c.data);
        setBalance(d.data.balance);
      })
      .catch((err) => toast.error(apiErr(err)));
  }, []);

  const toggleCrew = (id) => {
    setSelectedCrew((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= 3 ? prev : [...prev, id]
    );
  };

  const goToStake = async () => {
    if (selectedCrew.length !== 3) return toast.error("Select exactly three crew members");
    try {
      const { data } = await api.post(`/expeditions/${selectedExp.id}/estimate`, { crew_ids: selectedCrew });
      setEstimate(data);
      setStake(selectedExp.min_stake);
      setIdemKey((crypto.randomUUID && crypto.randomUUID()) || String(Date.now() + Math.random()));
      setStep("stake");
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const confirmStake = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/sessions", {
        expedition_id: selectedExp.id,
        crew_ids: selectedCrew,
        stake,
        idempotency_key: idemKey,
      });
      toast.success("Stake reserved. The expedition begins.");
      nav(`/mission/${data.id}`);
    } catch (e) {
      toast.error(apiErr(e));
      setBusy(false);
    }
  };

  return (
    <div className="space-y-5">
      <StepBar step={step} />

      <AnimatePresence mode="wait">
        {step === "map" && (
          <motion.div key="map" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <MapStep
              expeditions={expeditions}
              onSelect={(e) => {
                setSelectedExp(e);
                setSelectedCrew([]);
                setStep("crew");
              }}
            />
          </motion.div>
        )}

        {step === "crew" && selectedExp && (
          <motion.div key="crew" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <CrewStep
              exp={selectedExp}
              crew={crew}
              selected={selectedCrew}
              toggle={toggleCrew}
              onBack={() => setStep("map")}
              onNext={goToStake}
            />
          </motion.div>
        )}

        {step === "stake" && selectedExp && estimate && (
          <motion.div key="stake" initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }}>
            <StakeStep
              exp={selectedExp}
              crew={crew.filter((c) => selectedCrew.includes(c.id))}
              estimate={estimate}
              balance={balance}
              stake={stake}
              setStake={setStake}
              busy={busy}
              onBack={() => setStep("crew")}
              onCancel={() => nav("/dashboard")}
              onConfirm={confirmStake}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function StepBar({ step }) {
  const steps = [
    { id: "map", label: "Expedition" },
    { id: "crew", label: "Crew" },
    { id: "stake", label: "Stake" },
  ];
  const idx = steps.findIndex((s) => s.id === step);
  return (
    <div className="flex items-center gap-2">
      {steps.map((s, i) => (
        <React.Fragment key={s.id}>
          <div className={`flex items-center gap-2 text-xs ${i <= idx ? "text-ff-gold" : "text-ff-muted"}`}>
            <span className={`flex h-6 w-6 items-center justify-center rounded-full border text-[11px] ${i <= idx ? "border-ff-gold" : "border-ff-muted/40"}`}>
              {i + 1}
            </span>
            <span className="hidden sm:inline">{s.label}</span>
          </div>
          {i < steps.length - 1 && <div className={`h-px flex-1 ${i < idx ? "bg-ff-gold/60" : "bg-ff-brass/20"}`} />}
        </React.Fragment>
      ))}
    </div>
  );
}

function MapStep({ expeditions, onSelect }) {
  return (
    <div>
      <div className="mb-4 flex items-center gap-2">
        <MapTrifold size={24} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight">Choose Your Expedition</h1>
      </div>

      <div className="relative mb-6 overflow-hidden rounded-md border border-ff-brass/30">
        <div className="h-40 bg-cover bg-center sm:h-56" style={{ backgroundImage: `url(${HERO_MAP})` }} />
        <div className="absolute inset-0 bg-ff-bg/40" />
        <div className="animate-clouds pointer-events-none absolute inset-x-0 top-6 h-24 bg-[radial-gradient(ellipse_at_center,rgba(255,255,255,0.08),transparent_70%)]" />
        <svg className="pointer-events-none absolute inset-0 h-full w-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <polyline className="route-line" points="10,80 35,45 60,60 88,25" fill="none" stroke="#D4AF37" strokeWidth="0.6" strokeDasharray="4 4" opacity="0.6" vectorEffect="non-scaling-stroke" />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <p className="font-display text-2xl italic text-ff-parchment/80">The known world awaits</p>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-5 md:grid-cols-3">
        {expeditions.map((e, i) => (
          <motion.div
            key={e.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="group ff-panel overflow-hidden rounded-md transition-all duration-300 hover:-translate-y-1 hover:border-ff-gold/50"
            data-testid={`expedition-card-${e.id}`}
          >
            <div className="relative h-36 overflow-hidden">
              <img src={e.image} alt={e.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" />
              <div className="absolute inset-0 bg-gradient-to-t from-ff-bg2 to-transparent" />
              <span className={`absolute right-3 top-3 rounded-sm border bg-ff-bg/70 px-2 py-0.5 text-xs font-medium ${DIFF_COLOR[e.difficulty] || "text-ff-brass border-ff-brass/40"}`}>
                {e.difficulty}
              </span>
            </div>
            <div className="p-5">
              <p className="font-mono-ff text-[10px] uppercase tracking-widest text-ff-brass">{e.region}</p>
              <h3 className="font-display text-2xl font-semibold text-ff-parchment">{e.name}</h3>
              <p className="mt-2 text-sm leading-relaxed text-ff-muted">{e.description}</p>

              <dl className="mt-4 space-y-1.5 text-xs">
                <Row label="Stake" value={`${e.min_stake} – ${e.max_stake} Gold`} />
                <Row label="Reward" value={`${e.reward_min}x – ${e.reward_max}x`} />
                <Row label="Skills" value={e.primary_skills.join(", ")} />
              </dl>

              <div className="mt-3 flex items-start gap-1.5 rounded-sm bg-ff-danger/10 px-2 py-1.5 text-xs text-ff-danger/90">
                <Warning size={14} className="mt-0.5 shrink-0" />
                <span>{e.main_risk}</span>
              </div>

              <button
                onClick={() => onSelect(e)}
                data-testid={`select-expedition-${e.id}`}
                className="mt-4 w-full rounded-sm bg-ff-gold py-2.5 text-sm font-semibold text-ff-bg transition-all hover:bg-ff-brass active:scale-95"
              >
                Select
              </button>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-ff-muted">{label}</dt>
      <dd className="text-right font-medium text-ff-parchment">{value}</dd>
    </div>
  );
}

function CrewStep({ exp, crew, selected, toggle, onBack, onNext }) {
  return (
    <div>
      <button onClick={onBack} data-testid="crew-back-btn" className="mb-3 flex items-center gap-1 text-sm text-ff-muted hover:text-ff-gold">
        <ArrowLeft size={16} /> Back to map
      </button>
      <div className="mb-1 flex items-center gap-2">
        <Users size={24} weight="duotone" className="text-ff-gold" />
        <h1 className="font-display text-3xl font-bold tracking-tight">Select Your Crew</h1>
      </div>
      <p className="mb-4 text-sm text-ff-muted">
        {exp.name} favors <span className="text-ff-brass">{exp.primary_skills.join(", ")}</span>. Choose three ·{" "}
        <span data-testid="crew-selected-count" className="text-ff-gold">{selected.length}/3</span> selected
      </p>

      <GuidanceBanner id="crew-select" title="Building your crew">
        Pick exactly three. "Recommended" crew match this expedition's skills. Watch their loyalty, stress, and health — tired or injured crew can sit out until they recover at the Camp.
      </GuidanceBanner>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {crew.map((c) => {
          const active = selected.includes(c.id);
          const recommended = c.strong_skills.some((s) => exp.primary_skills.includes(s));
          const disabled = !c.available;
          return (
            <button
              key={c.id}
              onClick={() => !disabled && toggle(c.id)}
              disabled={disabled}
              data-testid={`crew-card-${c.id}`}
              className={`ff-panel relative rounded-md p-4 text-left transition-all duration-200 ${
                disabled ? "cursor-not-allowed opacity-55" : "hover:-translate-y-0.5"
              } ${active ? "border-ff-gold ff-glow-gold" : !disabled ? "hover:border-ff-brass/50" : ""}`}
            >
              {active && (
                <span className="absolute right-3 top-3 flex h-6 w-6 items-center justify-center rounded-full bg-ff-gold text-ff-bg">
                  <Check size={14} weight="bold" />
                </span>
              )}
              <div className="flex items-center gap-3">
                <img src={c.portrait} alt={c.name} className="h-16 w-16 rounded-sm object-cover" loading="lazy" />
                <div>
                  <div className="font-display text-lg font-semibold leading-tight text-ff-parchment">{c.name}</div>
                  <div className="flex items-center gap-2 text-[11px]">
                    <span className="rounded-sm bg-ff-gold/15 px-1.5 py-0.5 font-mono-ff text-ff-gold" data-testid={`crew-level-${c.id}`}>Lv {c.level}</span>
                    {recommended && <span className="font-medium uppercase tracking-wide text-ff-gold">Recommended</span>}
                  </div>
                </div>
              </div>

              {disabled ? (
                <div className="mt-3 rounded-sm bg-ff-danger/10 px-2 py-1.5 text-xs text-ff-danger/90" data-testid={`crew-unavailable-${c.id}`}>
                  <div className="flex items-start gap-1.5">
                    <Warning size={14} className="mt-0.5 shrink-0" />
                    <span>{c.recovery_reason || c.unavailable_reason}</span>
                  </div>
                  {c.recovery_eta_hours != null && (
                    <div className="mt-1 pl-5 text-ff-muted" data-testid={`crew-eta-${c.id}`}>
                      Ready in about {c.recovery_eta_hours < 1 ? "under an hour"
                        : c.recovery_eta_hours < 24 ? `${Math.round(c.recovery_eta_hours)} hours`
                        : `${Math.round(c.recovery_eta_hours)} hours`}
                      {c.treatment_can_shorten && " · Camp care can shorten this"}
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-xs leading-relaxed text-ff-muted line-clamp-2">{c.bio}</p>
              )}

              <div className="mt-3 flex flex-wrap gap-1.5 text-[11px]">
                {c.strong_skills.map((s) => (
                  <span key={s} className={`rounded-sm px-2 py-0.5 ${exp.primary_skills.includes(s) ? "bg-ff-gold/20 text-ff-gold" : "bg-ff-success/20 text-ff-success"}`}>
                    {s}
                  </span>
                ))}
                <span className="rounded-sm bg-ff-danger/15 px-2 py-0.5 text-ff-danger/90">Weak: {c.weakness}</span>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-[10px] text-ff-muted">
                <Meter label="Loyalty" value={c.loyalty} />
                <Meter label="Stress" value={c.stress} danger />
                <div className="text-center">
                  <div className={c.injury_status ? "text-ff-danger" : "text-ff-parchment"}>{c.injury_status ? c.injury_severity : c.health}</div>
                  <div>Health</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <button
        onClick={onNext}
        disabled={selected.length !== 3}
        data-testid="crew-next-btn"
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-sm bg-ff-gold py-3.5 font-semibold text-ff-bg transition-all hover:bg-ff-brass active:scale-95 disabled:cursor-not-allowed disabled:opacity-40 sm:w-auto sm:px-10"
      >
        Continue to Stake <ArrowRight size={18} />
      </button>
    </div>
  );
}

function Meter({ label, value, danger }) {
  return (
    <div className="text-center">
      <div className="mb-1 h-1.5 w-full overflow-hidden rounded-full bg-ff-bg">
        <div className={`h-full ${danger ? "bg-ff-danger" : "bg-ff-success"}`} style={{ width: `${value}%` }} />
      </div>
      <div>{label}</div>
    </div>
  );
}

function StakeStep({ exp, crew, estimate, balance, stake, setStake, busy, onBack, onCancel, onConfirm }) {
  const low = Math.max(5, estimate.success_low);
  const high = Math.min(97, estimate.success_high);
  const maxReward = Math.round(stake * exp.reward_max);
  const clamp = (v) => Math.max(exp.min_stake, Math.min(exp.max_stake, Math.min(v, balance)));

  return (
    <div className="mx-auto max-w-2xl">
      <button onClick={onBack} data-testid="stake-back-btn" className="mb-3 flex items-center gap-1 text-sm text-ff-muted hover:text-ff-gold">
        <ArrowLeft size={16} /> Back to crew
      </button>

      <GuidanceBanner id="stake" title="Reviewing your stake">
        This is the virtual Gold you're risking — it has no cash value. Check your balance and estimated odds, then confirm. You can never lose more than the stake shown here.
      </GuidanceBanner>

      <div className="ff-parchment-card rounded-md p-6 sm:p-8" data-testid="stake-confirmation">
        <div className="flex items-center gap-2 text-[#6b5323]">
          <Coins size={22} weight="duotone" />
          <span className="font-mono-ff text-xs uppercase tracking-widest">Stake Confirmation</span>
        </div>
        <h1 className="mt-1 font-display text-3xl font-bold text-[#1F1A12]">{exp.name}</h1>

        <div className="mt-5 grid grid-cols-2 gap-4 text-sm text-[#1F1A12]">
          <PRow label="Current Balance" value={<><GoldCounter value={balance} /> Gold</>} />
          <PRow label="Stake Limits" value={`${exp.min_stake} – ${exp.max_stake}`} />
          <PRow icon={Target} label="Estimated Success" value={`${low}% – ${high}%`} />
          <PRow icon={TrendUp} label="Max Possible Reward" value={`${maxReward} Gold`} />
        </div>

        <div className="mt-5 rounded-sm bg-[#e2d4b3] p-4">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-sm font-medium text-[#1F1A12]">Selected Stake</span>
            <span className="font-mono-ff text-2xl font-bold text-[#6b5323]" data-testid="stake-value">{stake}</span>
          </div>
          <input
            type="range"
            min={exp.min_stake}
            max={Math.min(exp.max_stake, balance)}
            value={stake}
            onChange={(e) => setStake(clamp(Number(e.target.value)))}
            data-testid="stake-slider"
            className="w-full accent-[#8a6d1f]"
          />
          <div className="mt-3 flex gap-2">
            <button
              onClick={() => setStake(clamp(stake - 50))}
              data-testid="reduce-stake-btn"
              className="flex-1 rounded-sm border border-[#8a6d1f]/50 py-2 text-sm font-medium text-[#6b5323] transition-colors hover:bg-[#8a6d1f]/10"
            >
              Reduce Stake (−50)
            </button>
            <button
              onClick={() => setStake(clamp(stake + 50))}
              data-testid="increase-stake-btn"
              className="flex-1 rounded-sm border border-[#8a6d1f]/50 py-2 text-sm font-medium text-[#6b5323] transition-colors hover:bg-[#8a6d1f]/10"
            >
              Increase Stake (+50)
            </button>
          </div>
        </div>

        <div className="mt-4 flex items-start gap-2 rounded-sm bg-[#8B2626]/10 p-3 text-xs text-[#7a2020]">
          <Warning size={16} className="mt-0.5 shrink-0" />
          <span>{exp.main_risk} You can never lose more than your confirmed stake.</span>
        </div>

        <div className="mt-4">
          <p className="mb-2 text-xs font-medium uppercase tracking-wide text-[#6b5323]">Selected Crew</p>
          <div className="flex gap-2">
            {crew.map((c) => (
              <div key={c.id} className="flex items-center gap-2 rounded-sm bg-[#e2d4b3] px-2 py-1">
                <img src={c.portrait} alt={c.name} className="h-8 w-8 rounded-sm object-cover" />
                <span className="text-xs font-medium text-[#1F1A12]">{c.name.split(" ").slice(-1)}</span>
              </div>
            ))}
          </div>
        </div>

        <p className="mt-4 text-center text-xs italic text-[#6b5323]">
          Your choices, crew, and skill affect the outcome. A limited chance factor is also included.
        </p>

        <div className="mt-5 flex flex-col gap-2 sm:flex-row">
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="confirm-stake-btn"
            className="flex flex-1 items-center justify-center gap-2 rounded-sm bg-[#2D5A27] py-3.5 font-semibold text-[#F2E8D3] transition-all hover:bg-[#26491f] active:scale-95 disabled:opacity-60"
          >
            <Check size={18} /> {busy ? "Reserving..." : "Confirm Stake"}
          </button>
          <button
            onClick={onCancel}
            data-testid="cancel-stake-btn"
            className="flex items-center justify-center gap-2 rounded-sm border border-[#8a6d1f]/50 px-6 py-3.5 font-semibold text-[#6b5323] transition-colors hover:bg-[#8a6d1f]/10"
          >
            <X size={18} /> Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function PRow({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-1 text-xs text-[#6b5323]">
        {Icon && <Icon size={13} />} {label}
      </div>
      <div className="font-mono-ff text-lg font-bold text-[#1F1A12]">{value}</div>
    </div>
  );
}
